// The development page's Building management tab (the demo's "23a. BUILDING MANAGEMENT — the page"): a hero with the
// building's photo, facts and how complete its record is, then At a glance | Building record | Plant & equipment |
// Contracts | Budget & charges (?sub=). The front page answers "what needs doing and who do I call"; the other sub-tabs
// are the record, plant, contracts and budget screens the app already has, narrowed to this building. Each sub-tab and
// each number shows only to a user who may read the page behind it.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, ChevronRight, MapPin, Phone } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { useStatFilter } from '../../hooks/useStatFilter';
import { useListView } from '../../hooks/useListView';
import { StatTile } from '../charts/StatTile';
import { Tabs, Button, Card, type MenuEntry } from '../ui';
import { FormError } from '../forms/common';
import { AssetForm } from '../forms/AssetForm';
import { BudgetEditor } from '../accounts/BudgetEditor';
import { RecordTab } from '../building/RecordTab';
import { ContractsPanel } from '../building/ContractsPanel';
import { Chip } from '../building/bits';
import { PlantPanel } from './PlantPanel';
import { BudgetChargesPanel } from './BudgetChargesPanel';
import { buildingApi, CONTRACT_STATE_COLOUR, DUE_COLOUR, telHref } from '../../lib/building';
import { buildingPhoto } from '../../lib/site';
import { demandLabel } from '../../lib/fees';
import { formatDate, formatMoney } from '../../lib/viz';
import type { BuildingGlanceLink, BuildingSubTab, BuildingSummary } from '../../types/api';
import { formatPhone } from '../../lib/phone';

const SHOWN = 14;
type P = Record<string, any>;

/** The completion ring (demo .ring "<pct>% record"): an SVG arc in the accent colour. */
function RecordRing({ pct, size = 84 }: { pct: number; size?: number }) {
  const r = (size - 10) / 2; const c = 2 * Math.PI * r;
  return (
    <div className="relative grid shrink-0 place-items-center" style={{ width: size, height: size }} role="img" aria-label={`Building record ${pct}% complete`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={7} className="stroke-ink-100" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={7} strokeLinecap="round" className="stroke-bronze-500" strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, Math.max(0, pct)) / 100)} />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-center leading-none">
        <span><span className="block text-[17px] font-semibold tabular-nums text-ink-900">{pct}%</span><span className="mt-0.5 block text-[9.5px] uppercase tracking-[0.14em] text-ink-400">record</span></span>
      </span>
    </div>
  );
}

function SectionHead({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{children}</h3>
      <span className="h-px flex-1 bg-ink-100" />
      {count !== undefined && <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">{count}</span>}
    </div>
  );
}

const addressOf = (p: P) => {
  const a = (p.address ?? {}) as Record<string, unknown>;
  const parts = [a.line1, a.line2, a.street, a.city ?? p.city, a.postcode ?? p.postcode].map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean);
  return [...new Set(parts)].join(', ');
};
const photoOf = (p: P): string | null => {
  const media = Array.isArray(p.media) ? (p.media as Array<{ url?: string; kind?: string }>) : [];
  return media.find((m) => m.url && (!m.kind || m.kind === 'IMAGE'))?.url ?? buildingPhoto(p.slug);
};

function Hero({ property, s }: { property: P; s: BuildingSummary | undefined }) {
  const [broken, setBroken] = useState(false);
  const photo = photoOf(property);
  const facts = s ? ([['Units', s.facts.units], ['Blocks', s.facts.blocks], ['Floors', s.facts.floors], ['Tenure', s.facts.tenure], ['Completed', s.facts.completed]] as const).filter((f) => f[1]) : [];
  return (
    <div className="surface flex flex-col gap-4 overflow-hidden p-3 sm:flex-row sm:items-center sm:p-4">
      <div className="grid h-36 w-full shrink-0 place-items-center overflow-hidden rounded-xl bg-ink-100 sm:h-[104px] sm:w-[150px]">
        {photo && !broken ? <img src={photo} alt="" className="h-full w-full object-cover" onError={() => setBroken(true)} /> : <Building2 className="h-8 w-8 text-ink-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-semibold leading-tight text-ink-900">{property.name}</h2>
            {addressOf(property) && <p className="mt-1 flex items-start gap-1 text-[12.5px] text-ink-500"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />{addressOf(property)}</p>}
          </div>
          {s?.record && <div className="sm:hidden"><RecordRing pct={s.record.pct} size={64} /></div>}
        </div>
        {facts.length > 0 && (
          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            {facts.map(([k, v]) => <div key={k}><dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">{k}</dt><dd className="text-[14px] font-semibold text-ink-900">{v}</dd></div>)}
          </dl>
        )}
      </div>
      {s?.record && <div className="hidden pr-2 sm:block"><RecordRing pct={s.record.pct} /></div>}
    </div>
  );
}

/** One line on "What needs doing". */
interface Todo { key: string; date: string | null; title: string; sub: string | null; colour: string; chip: string; overdue: boolean; go: () => void }

export function BuildingManagementTab({ property, params, setParam, onMenu }: {
  property: P;
  params: URLSearchParams;
  /** Merge into the page's query string (?sub=, ?section=, ?contract=, ?view=, ?bmf=). */
  setParam: (patch: Record<string, string | null>) => void;
  /** Hand "Add asset", "Add contract"… up to the page's ⋯ menu. */
  onMenu?: (m: MenuEntry[]) => void;
}) {
  const propertyId = property.id as string;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canRecord = useCan('buildingRecord.read'); const canAssets = useCan('assets.read'); const canContracts = useCan('contracts.read'); const canBudgets = useCan('budgets.read');
  const canAddAsset = useCan('assets.create');
  const summary = useQuery({ queryKey: ['building-summary', propertyId], queryFn: () => buildingApi.summary(propertyId) });
  const s = summary.data;

  const SUBS = useMemo(() => ([
    ['glance', 'At a glance', true], ['record', 'Building record', canRecord], ['assets', 'Plant & equipment', canAssets],
    ['contracts', 'Contracts', canContracts], ['money', 'Budget & charges', canBudgets],
  ] as Array<[BuildingSubTab, string, boolean]>).filter((x) => x[2]), [canRecord, canAssets, canContracts, canBudgets]);
  const sub: BuildingSubTab = SUBS.find((x) => x[0] === params.get('sub'))?.[0] ?? 'glance';
  // The record's View (calendar / cards): ?view=cards, else what this browser chose last.
  const [recordView, setRecordView] = useListView('record.view', ['calendar', 'cards'] as const, 'calendar', params.get('view'), (v) => setParam({ view: v }));
  const go = (k: BuildingSubTab, extra: Record<string, string | null> = {}) => setParam({ sub: k === 'glance' ? null : k, section: null, contract: null, view: null, bmf: null, filter: null, ...extra });
  const follow = (l: BuildingGlanceLink) => {
    if (l.tab === 'management') return setParam({ tab: 'management', sub: l.sub, section: null, contract: null, view: null, bmf: null, filter: null });
    if (l.assetId) return navigate(`/app/assets/${l.assetId}`);
    return go(l.sub as BuildingSubTab, { section: l.sectionId ?? null, contract: l.contractId ?? null });
  };

  const [addingAsset, setAddingAsset] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  // The Contracts panel's own entries (add, export with its filters) while that sub-tab is open.
  const [contractMenu, setContractMenu] = useState<MenuEntry[]>([]);
  useEffect(() => {
    if (!onMenu) return;
    const addAsset = (separator = false): MenuEntry => ({ label: 'Add asset', icon: 'Plus', can: 'assets.create', separator, onClick: () => setAddingAsset(true) });
    const exportAssets = (separator = false): MenuEntry => ({ label: 'Export assets CSV', icon: 'Download', separator, export: { path: '/assets', page: 'assets', query: { propertyId }, filename: 'assets' } });
    const addContract: MenuEntry = { label: 'Add contract', icon: 'FileText', can: 'contracts.create', onClick: () => go('contracts', { contract: 'new' }) };
    const editBudget: MenuEntry = { label: 'Edit budget', icon: 'Wallet', can: 'budgets.update', onClick: () => setEditingBudget(true) };
    // On the Contracts sub-tab its own "Add a contract" and "Export CSV" (with the filters shown) come first.
    onMenu(sub === 'contracts' && contractMenu.length
      ? [...contractMenu, addAsset(true), editBudget, exportAssets()]
      : [addAsset(), addContract, editBudget, exportAssets(true)]);
    return () => onMenu([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, onMenu, sub, contractMenu]);

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['building-summary', propertyId] }); };

  return (
    <div className="space-y-2.5">
      <Hero property={property} s={s} />
      {summary.error && <FormError error={summary.error} />}

      <Tabs items={SUBS.map(([k, l]) => ({ key: k, label: l }))} value={sub} onChange={(k) => go(k)} />

      {sub === 'glance' && <Glance s={s} loading={summary.isLoading} follow={follow} go={go} setParam={setParam} navigate={navigate} />}

      {sub === 'record' && (
        <RecordTab propertyId={propertyId} section={params.get('section')} onSection={(sid) => setParam({ section: sid })}
          onTab={(t) => go(t === 'contracts' ? 'contracts' : 'record')} onContract={(cid) => go('contracts', { contract: cid })}
          view={recordView} onView={setRecordView} />
      )}

      {sub === 'assets' && <PlantPanel propertyId={propertyId} />}

      {sub === 'contracts' && <ContractsPanel propertyId={propertyId} openId={params.get('contract')} onOpen={(cid) => { setParam({ contract: cid }); if (!cid) refresh(); }} onMenu={setContractMenu} />}

      {sub === 'money' && (s?.money
        ? <BudgetChargesPanel money={s.money} units={s.facts.units} onEdit={() => setEditingBudget(true)} />
        : <div className="surface h-40 animate-pulse" />)}

      {addingAsset && canAddAsset && <AssetForm open propertyId={propertyId} onClose={() => { setAddingAsset(false); refresh(); }} />}
      {editingBudget && canBudgets && <BudgetEditor propertyId={propertyId} year={s?.money?.year ?? new Date().getUTCFullYear()} open onClose={() => { setEditingBudget(false); refresh(); }} />}
    </div>
  );
}

function Glance({ s, loading, follow, go, setParam, navigate }: {
  s: BuildingSummary | undefined; loading: boolean;
  follow: (l: BuildingGlanceLink) => void;
  go: (k: BuildingSubTab, extra?: Record<string, string | null>) => void;
  setParam: (patch: Record<string, string | null>) => void;
  navigate: (to: string) => void;
}) {
  // Overdue / Due in 90 days narrow the list below (?bmf=overdue|soon).
  const tf = useStatFilter<'overdue' | 'soon'>('bmf');
  if (loading || !s) {
    return <div className="space-y-2.5"><div className="tile-row">{Array.from({ length: 6 }, (_, i) => <div key={i} className="surface h-[78px] animate-pulse" />)}</div><div className="surface h-48 animate-pulse" /></div>;
  }
  const c = s.glance.counts;
  const toManagement = (sub: string, pmf: string | null) => setParam({ tab: 'management', sub, pmf, section: null, contract: null, view: null, bmf: null, filter: null });
  const tiles: Array<{ key: string; label: string; value: string | null; status?: 'critical' | 'warning'; hint?: string; active?: boolean; onClick?: () => void }> = [
    { key: 'overdue', label: 'Overdue', value: c.overdue === null ? null : String(c.overdue), status: c.overdue ? 'critical' : undefined, ...tf.tile('overdue') },
    { key: 'soon', label: 'Due in 90 days', value: c.dueIn90 === null ? null : String(c.dueIn90), status: c.dueIn90 ? 'warning' : undefined, ...tf.tile('soon') },
    { key: 'certs', label: 'Certificates expired', value: c.certsExpired === null ? null : String(c.certsExpired), status: c.certsExpired ? 'critical' : undefined, onClick: () => toManagement('compliance', 'EXPIRED') },
    { key: 'works', label: 'Open works', value: c.openWorks === null ? null : String(c.openWorks), onClick: () => toManagement('works', 'open') },
    { key: 'assets', label: 'Assets logged', value: c.assetsLogged === null ? null : String(c.assetsLogged), hint: c.assetsDue ? `${c.assetsDue} due service` : undefined, onClick: () => go('assets') },
    { key: 'contracts', label: 'Contracts', value: c.contracts === null ? null : String(c.contracts), hint: c.section20 ? `${c.section20} need s20` : undefined, onClick: () => go('contracts') },
    { key: 'flagged', label: 'Contracts flagged', value: c.contractsFlagged === null ? null : String(c.contractsFlagged), status: c.contractsFlagged ? 'warning' : undefined, hint: 'Notice open / ends soon', onClick: () => go('contracts') },
    { key: 'committed', label: 'Committed pa', value: c.committedMinor === null ? null : formatMoney(c.committedMinor, c.currency), onClick: () => go('contracts') },
  ];

  // What needs doing: the dated things, then any plant or contract that needs action and has no date line of its own.
  const todos: Todo[] = (s.glance.keyDates ?? []).map((d) => ({
    key: d.id, date: d.date, title: d.label, sub: d.sub, colour: DUE_COLOUR[d.due.key], chip: d.due.label, overdue: d.due.key === 'overdue', go: () => follow(d.link),
  }));
  const seen = new Set((s.glance.keyDates ?? []).flatMap((d) => [d.link.assetId, d.link.contractId].filter(Boolean) as string[]));
  for (const a of s.glance.assetsDue ?? []) {
    if (seen.has(a.id)) continue;
    todos.push({ key: `asset-${a.id}`, date: a.nextServiceDue, title: `${a.name} service due`, sub: a.location ?? 'Plant & equipment', colour: DUE_COLOUR[a.due.key], chip: a.due.label, overdue: a.due.key === 'overdue', go: () => navigate(`/app/assets/${a.id}`) });
  }
  for (const k of s.glance.contractsFlagged ?? []) {
    if (seen.has(k.id)) continue;
    todos.push({ key: `contract-${k.id}`, date: k.noticeBy ?? k.endDate, title: `${k.serviceLabel} — ${k.state.label.toLowerCase()}`, sub: k.supplier, colour: CONTRACT_STATE_COLOUR[k.state.key as keyof typeof CONTRACT_STATE_COLOUR] ?? DUE_COLOUR.soon, chip: k.state.label, overdue: false, go: () => go('contracts', { contract: k.id }) });
  }
  todos.sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999'));
  const shown = todos.filter((t) => !tf.active || (tf.is('overdue') ? t.overdue : !t.overdue));
  const who = s.glance.whoToCall;
  const m = s.money;

  return (
    <div className="space-y-2.5">
      <div className="tile-row">
        {tiles.filter((t) => t.value !== null).map((t) => <StatTile key={t.key} compact label={t.label} value={t.value} status={t.status} hint={t.hint} active={t.active} onClick={t.onClick} />)}
      </div>

      <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
        <section className="min-w-0">
          <SectionHead count={shown.length}>{tf.is('overdue') ? 'Overdue' : tf.is('soon') ? 'Due in the next 90 days' : 'What needs doing'}</SectionHead>
          {s.glance.keyDates === null && !s.glance.assetsDue && !s.glance.contractsFlagged
            ? <div className="surface px-4 py-8 text-center text-[12.5px] text-ink-500">Your role does not include the building record, plant or contracts.</div>
            : shown.length ? (
              <div className="surface divide-y divide-ink-100 overflow-hidden">
                {shown.slice(0, SHOWN).map((t) => (
                  <button key={t.key} type="button" onClick={t.go} className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-bronze-50/50" style={{ boxShadow: `inset 3px 0 0 ${t.colour}` }}>
                    <span className="hidden w-[78px] shrink-0 text-[11.5px] tabular-nums text-ink-500 sm:block">{t.date ? formatDate(t.date) : '—'}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-ink-800" title={t.title}>{t.title}</span>
                      <span className="block truncate text-[11.5px] text-ink-400">{t.date && <span className="sm:hidden">{formatDate(t.date)} · </span>}{t.sub}</span>
                    </span>
                    <Chip colour={t.colour}>{t.chip}</Chip>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-300" />
                  </button>
                ))}
                {shown.length > SHOWN && <p className="px-3 py-2 text-[11.5px] text-ink-400">…and {shown.length - SHOWN} further ahead.</p>}
              </div>
            ) : <div className="surface px-4 py-8 text-center text-[12.5px] text-ink-500">{tf.active ? 'Nothing here.' : 'Nothing dated in the next 90 days. Renewal dates on the building record, plant service dates and contract notice deadlines all appear here.'}</div>}
        </section>

        <aside className="min-w-0 space-y-2.5">
          {who && (
            <section>
              <SectionHead>Who to call</SectionHead>
              {who.length ? (
                <div className="surface divide-y divide-ink-100">
                  {who.map((w, i) => (
                    <div key={`${w.role}-${i}`} className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ink-400">{w.emergency && <AlertTriangle className="h-3 w-3 text-red-500" />}{w.role}</div>
                      <div className="text-[13px] text-ink-800">{w.name ?? '—'}</div>
                      <div className="flex flex-wrap items-center gap-x-3">
                        {w.phone && <a href={telHref(w.phone)} className="inline-flex items-center gap-1 text-[12.5px] font-medium tabular-nums text-bronze-700 hover:underline"><Phone className="h-3 w-3" />{formatPhone(w.phone)}</a>}
                        {w.email && <a href={`mailto:${w.email}`} className="min-w-0 truncate text-[12px] text-ink-500 hover:underline">{w.email}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="surface px-4 py-6 text-center text-[12.5px] text-ink-500">Add the property manager and out-of-hours numbers on Development Overview in the building record and they appear here.</div>}
            </section>
          )}

          <Card title="Development overview" actions={s.record ? <button type="button" onClick={() => go('record', { section: 'overview' })} className="text-[12px] font-medium text-bronze-600 hover:underline">Edit</button> : undefined}>
            <dl className="-my-1 divide-y divide-ink-100 text-[12.5px]">
              {([['Units', s.facts.units], ['Blocks', s.facts.blocks], ['Floors', s.facts.floors], ['Tenure', s.facts.tenure], ['Completed', s.facts.completed]] as const).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 py-1.5"><dt className="text-ink-500">{k}</dt><dd className="font-medium text-ink-900">{v ?? '—'}</dd></div>
              ))}
              {s.record && <div className="flex justify-between gap-3 py-1.5"><dt className="text-ink-500">Building record</dt><dd className="font-medium text-ink-900">{s.record.pct}% · {s.record.filled}/{s.record.total}</dd></div>}
            </dl>
          </Card>

          {m && (
            <Card title={`Service charge ${m.year}`} actions={<button type="button" onClick={() => go('money')} className="text-[12px] font-medium text-bronze-600 hover:underline">Open</button>}>
              <dl className="-my-1 divide-y divide-ink-100 text-[12.5px]">
                {([
                  ['Budget', m.budgetMinor === null ? 'Not set' : `${formatMoney(m.budgetMinor, m.currency)}${m.status === 'DRAFT' ? ' (draft)' : ''}`],
                  ['Year end', m.yearEnd ? formatDate(m.yearEnd) : '—'],
                  ['Demands', demandLabel(m.demandFrequency) ?? '—'],
                  ['Next demand', m.nextDemand ? formatDate(m.nextDemand) : '—'],
                  ['Reserve fund', m.reserveBalanceMinor === null ? '—' : formatMoney(m.reserveBalanceMinor, m.currency)],
                ] as const).map(([k, v]) => <div key={k} className="flex justify-between gap-3 py-1.5"><dt className="text-ink-500">{k}</dt><dd className="font-medium text-ink-900">{v}</dd></div>)}
              </dl>
              {m.budgetMinor === null && <Button size="sm" variant="secondary" className="mt-3 w-full" onClick={() => go('money')}>Set this year's budget</Button>}
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
